import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, FileText, Truck, X } from 'lucide-react';
import type { AppNotification, NotificationType } from '@iomi/shared';

interface NotificationPanelProps {
  notifications: AppNotification[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  onClose: () => void;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  onLoadMore: () => void;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const URGENT_TYPES: NotificationType[] = ['quote_response_received'];
const HIGH_TYPES: NotificationType[] = ['quote_lead_assigned', 'move_lead_assigned', 'quote_booked'];

function getAccentClass(type: NotificationType, read: boolean): string {
  if (read) return '';
  if (URGENT_TYPES.includes(type)) return 'border-l-2 border-rose';
  if (HIGH_TYPES.includes(type)) return 'border-l-2 border-plum';
  return 'border-l-2 border-stone';
}

function getIcon(type: NotificationType) {
  switch (type) {
    case 'job_status_changed':
    case 'move_lead_assigned':
      return <Truck size={16} className="text-slate shrink-0" />;
    default:
      return <FileText size={16} className="text-slate shrink-0" />;
  }
}

function getNavigationPath(n: AppNotification): string {
  return `/moves/${n.resourceId}`;
}

export function NotificationPanel({
  notifications,
  loading,
  error,
  hasMore,
  onClose,
  onMarkRead,
  onMarkAllRead,
  onLoadMore,
}: NotificationPanelProps) {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  function handleNotificationClick(n: AppNotification) {
    if (!n.read) onMarkRead(n.id);
    onClose();
    navigate(getNavigationPath(n));
  }

  const hasUnread = notifications.some(n => !n.read);

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-1.5 z-50 w-[340px] max-w-[calc(100vw-2rem)]
                 border border-stone rounded-xl shadow-brand-md overflow-hidden"
      style={{ backgroundColor: 'var(--color-surface, #fff)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-stone">
        <h3 className="text-sm font-semibold text-primary-theme">Notifications</h3>
        <div className="flex items-center gap-1">
          {hasUnread && (
            <button
              type="button"
              onClick={onMarkAllRead}
              className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-plum
                         hover:bg-plum/5 rounded-md transition-colors"
              title="Mark all as read"
            >
              <CheckCheck size={13} />
              Mark all read
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-slate hover:text-primary-theme rounded-md
                       hover:bg-mist transition-colors"
            aria-label="Close notifications"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* List */}
      <div className="max-h-[360px] overflow-y-auto">
        {loading && notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate">
            Loading...
          </div>
        ) : error ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-rose">Failed to load notifications</p>
            <p className="text-[12px] text-slate mt-1">Please try again later</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Bell size={24} className="mx-auto mb-2 text-stone" />
            <p className="text-sm text-slate">You're all caught up</p>
          </div>
        ) : (
          <>
            {notifications.map(n => (
              <button
                key={n.id}
                type="button"
                onClick={() => handleNotificationClick(n)}
                className={`w-full text-left px-4 py-3 flex items-start gap-3
                           hover:bg-mist transition-colors
                           ${!n.read ? 'bg-plum/[0.03]' : ''}
                           ${getAccentClass(n.type, n.read)}`}
                style={{ minHeight: '44px' }}
              >
                <div className="mt-0.5">
                  {getIcon(n.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-[13px] leading-tight ${!n.read ? 'font-semibold text-primary-theme' : 'font-medium text-slate'}`}>
                      {n.title}
                    </p>
                    {!n.read && (
                      <span className="mt-1 w-2 h-2 rounded-full bg-plum shrink-0" />
                    )}
                  </div>
                  <p className="text-[12px] text-slate mt-0.5 truncate">{n.message}</p>
                  <p className="text-[11px] text-slate/70 mt-1">{timeAgo(n.createdAt)}</p>
                </div>
              </button>
            ))}

            {hasMore && (
              <button
                type="button"
                onClick={onLoadMore}
                className="w-full px-4 py-3 text-[13px] font-medium text-plum
                           hover:bg-mist transition-colors text-center"
              >
                Load more
              </button>
            )}
          </>
        )}
      </div>

      {/* Footer — link to full page */}
      <button
        type="button"
        onClick={() => { onClose(); navigate('/notifications'); }}
        className="w-full px-4 py-2.5 text-[13px] font-medium text-plum
                   hover:bg-mist transition-colors text-center border-t border-stone"
      >
        View all notifications
      </button>
    </div>
  );
}
